import { useTranslation } from "react-i18next";
import { Box, Stack, ToggleButton } from "@mui/material";
import { FoodsIcon, SearchBox, Typography } from "@foodpilot/foods";

export type PermissionsToolbarProps = {
    search: string;
    onSearchChange: (value: string) => void;
    activeOnly: boolean;
    onActiveOnlyChange: (value: boolean) => void;
    activeCount: number;
};

export const PermissionsToolbar = (props: PermissionsToolbarProps) => {
    const { search, onSearchChange, activeOnly, onActiveOnlyChange, activeCount } = props;
    const { t } = useTranslation();

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
        >
            <Box
                sx={(theme) => ({
                    minWidth: theme.spacing(35),
                    flex: `1 1 ${theme.spacing(35)}`,
                    maxWidth: theme.spacing(52.5),
                })}
            >
                <SearchBox
                    value={search}
                    onChange={onSearchChange}
                    placeholder={t("settings.roles.search_placeholder", "Filter by resource or action")}
                    variant="standalone"
                />
            </Box>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body-medium" role="status" sx={(theme) => ({ color: theme.custom.grey[2500] })}>
                    {t("settings.roles.active_count", "{{count}} active", { count: activeCount })}
                </Typography>
                <ToggleButton
                    value="active-only"
                    selected={activeOnly}
                    onChange={() => onActiveOnlyChange(!activeOnly)}
                    aria-pressed={activeOnly}
                    aria-label={t("settings.roles.active_only", "Active")}
                    size="small"
                    disableRipple
                    sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        gap: theme.spacing(0.75),
                        height: theme.spacing(4.5),
                        padding: theme.spacing(0, 1.75),
                        borderRadius: theme.spacing(1),
                        textTransform: "none",
                        ...theme.typography["small-bold"],
                        transition: "background-color 150ms, border-color 150ms, color 150ms",
                        "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                        },
                        border: `1px solid ${activeOnly ? theme.palette.primary[600] : theme.custom.grey[600]}`,
                        color: activeOnly ? theme.custom.grey[100] : theme.custom.grey[2500],
                        backgroundColor: activeOnly ? theme.palette.primary[600] : theme.custom.grey[100],
                        "&.Mui-selected": {
                            backgroundColor: theme.palette.primary[600],
                            color: theme.custom.grey[100],
                            "&:hover": {
                                backgroundColor: theme.palette.primary[700],
                            },
                        },
                        "&:hover": {
                            backgroundColor: activeOnly ? theme.palette.primary[700] : theme.custom.grey[300],
                        },
                        "&:focus-visible": {
                            outline: `2px solid ${theme.palette.primary[400]}`,
                            outlineOffset: "1px",
                        },
                    })}
                >
                    <FoodsIcon icon={activeOnly ? "check" : "filter"} size={1} />
                    {t("settings.roles.active_only", "Active")}
                </ToggleButton>
            </Stack>
        </Stack>
    );
};
